import { Fragment, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dialog, Transition, Disclosure } from '@headlessui/react';
import {
  XMarkIcon,
  Bars3Icon,
  HomeIcon,
  ChartBarIcon,
  ChevronDownIcon,
  WrenchScrewdriverIcon,
  EnvelopeIcon,
  StarIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';


const navigation = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: HomeIcon,
  },
  {
    name: 'Scores',
    href: '/leagues',
    icon: ChartBarIcon,
    children: [
      { name: 'NFL', href: '/leagues/nfl' },
      { name: 'NBA', href: '/leagues/nba' },
      { name: 'NHL', href: '/leagues/nhl' },
      { name: 'MLB', href: '/leagues/mlb' },
      { name: 'NCAA Football', href: '/leagues/ncaa-f' },
      { name: 'NCAA Basketball', href: '/leagues/ncaa-b' },
    ],
  },
  {
    name: 'Picks & Edges',
    href: '/top-picks',
    icon: StarIcon,
    premium: true,
  },
  {
    name: 'Parlay Workshop',
    href: '/parlay-workshop',
    icon: WrenchScrewdriverIcon,
    premium: true,
  },
  // Bet Slips hidden - feature not complete
  // Models hidden - feature not complete
  // Newsletter hidden for now
];


function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function MobileNav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();


  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 glass-card border-b">
        <div className="flex items-center justify-between p-4">
          <h1 className="text-2xl font-black text-gradient">UKnew</h1>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center justify-center p-2 rounded-lg text-text-secondary hover:text-accent-blue hover:bg-bg-card-hover transition-all duration-200"
          >
            <span className="sr-only">Open menu</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="lg:hidden" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-bg-primary/80 backdrop-blur-sm z-40" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>

                {/* Sidebar component */}
                <div className="flex grow flex-col gap-y-5 overflow-y-auto glass-card px-6 pb-4 scrollbar-thin">
                  <div className="flex h-16 shrink-0 items-center">
                    <h1 className="text-2xl font-black text-gradient">UKnew</h1>
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul className="space-y-1">
                          {navigation.map((item) => {
                            if (item.children) {
                              return (
                                <Disclosure as="li" key={item.name}>
                                  {({ open }) => (
                                    <>
                                      <Disclosure.Button
                                        className={classNames(
                                          'group w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg',
                                          'hover:bg-bg-card-hover transition-all duration-200'
                                        )}
                                      >
                                        <div className="flex items-center">
                                          <item.icon
                                            className="mr-3 h-6 w-6 text-text-secondary group-hover:text-accent-blue"
                                            aria-hidden="true"
                                          />
                                          <span className="text-text-primary">{item.name}</span>
                                        </div>
                                        <ChevronDownIcon
                                          className={classNames(
                                            'h-5 w-5 text-text-secondary transition-transform duration-200',
                                            open ? 'transform rotate-180' : ''
                                          )}
                                        />
                                      </Disclosure.Button>
                                      <Disclosure.Panel className="mt-1 space-y-1 pl-11">
                                        {item.children.map((subItem) => {
                                          const isActive = location.pathname === subItem.href;
                                          return (
                                            <Link
                                              key={subItem.name}
                                              to={subItem.href}
                                              onClick={() => setOpen(false)}
                                              className={classNames(
                                                'block px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                                                isActive
                                                  ? 'bg-accent-blue/10 text-accent-blue border-l-4 border-accent-blue'
                                                  : 'text-text-secondary hover:bg-bg-card-hover hover:text-text-primary'
                                              )}
                                            >
                                              {subItem.name}
                                            </Link>
                                          );
                                        })}
                                      </Disclosure.Panel>
                                    </>
                                  )}
                                </Disclosure>
                              );
                            }

                            const isActive = location.pathname === item.href;
                            return (
                              <li key={item.name}>
                                <Link
                                  to={item.href}
                                  onClick={() => setOpen(false)}
                                  className={classNames(
                                    'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                                    isActive
                                      ? 'bg-accent-blue/10 text-accent-blue border-l-4 border-accent-blue'
                                      : 'text-text-primary hover:bg-bg-card-hover'
                                  )}
                                >
                                  <item.icon
                                    className={classNames(
                                      'mr-3 h-6 w-6',
                                      isActive
                                        ? 'text-accent-blue'
                                        : 'text-text-secondary group-hover:text-accent-blue'
                                    )}
                                    aria-hidden="true"
                                  />
                                  <span className="flex-1">{item.name}</span>
                                  {item.premium && (
                                    <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded">
                                      PRO
                                    </span>
                                  )}
                                  {item.badge && (
                                    <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-accent-blue to-accent-purple text-white rounded animate-pulse">
                                      {item.badge}
                                    </span>
                                  )}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    </ul>
                  </nav>

                  {/* Account & Footer */}
                  <div className="py-4 border-t border-border space-y-3">
                    <Link
                      to="/account"
                      onClick={() => setOpen(false)}
                      className="group flex items-center px-3 py-2 text-sm font-medium rounded-lg text-text-secondary hover:bg-bg-card-hover hover:text-text-primary transition-all duration-200"
                    >
                      <Cog6ToothIcon className="mr-3 h-5 w-5 text-text-secondary group-hover:text-accent-blue" />
                      Account Settings
                    </Link>
                    <a
                      href="mailto:support@uknew.net"
                      className="group flex items-center px-3 py-2 text-sm font-medium rounded-lg text-text-secondary hover:bg-bg-card-hover hover:text-text-primary transition-all duration-200"
                    >
                      <EnvelopeIcon className="mr-3 h-5 w-5 text-text-secondary group-hover:text-accent-blue" />
                      Contact Us
                    </a>
                    <p className="text-xs text-text-secondary text-center">
                      Powered by AI Predictions
                    </p>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
